import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Layout
import Layout from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import LandingPage from './pages/LandingPage';
import MusiciansPage from './pages/MusiciansPage';
import MyMusicianProfilePage from './pages/MyMusicianProfilePage';
import MusicianAvailabilityPage from './pages/MusicianAvailabilityPage';
import TechPage from './pages/TechPage';
import MyTechProfilePage from './pages/MyTechProfilePage';
import TechAvailabilityPage from './pages/TechAvailabilityPage';
import VenuesPage from './pages/VenuesPage';
import VenueDetailPage from './pages/VenueDetailPage';
import MyVenueProfilePage from './pages/MyVenueProfilePage';
import MyVenueBacklinePage from './pages/MyVenueBacklinePage';
import RequestsPage from './pages/RequestsPage';
import CreateRequestPage from './pages/CreateRequestPage';
import EditRequestPage from './pages/EditRequestPage';
import ManageRequestsPage from './pages/ManageRequestsPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import MyInterestsPage from './pages/MyInterestsPage';
import SavedRequestsPage from './pages/SavedRequestsPage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import PendingInvitesPage from './pages/PendingInvitesPage';
import TestDashboardPage from './pages/TestDashboardPage';
import WhatsNewPage from './pages/WhatsNewPage';
import GigSlugPage from './pages/GigSlugPage';
import RiderCheckRunPage from './pages/RiderCheckRunPage';
import JoinPage from './pages/JoinPage';
import PrivacyPage from './pages/PrivacyPage';
import MyRiderPage from './pages/MyRiderPage';
import NotFoundPage from './pages/NotFoundPage';

// Onboarding Wizards
import MusicianOnboardingPage from './pages/onboarding/MusicianOnboardingPage';
import TechOnboardingPage from './pages/onboarding/TechOnboardingPage';
import VenueOnboardingPage from './pages/onboarding/VenueOnboardingPage';
import { AppShellSkeleton } from './components/PageSkeleton';

// Protected Route
const MUSICIAN_TYPES = ['MUSICIAN'];
const TECH_TYPES = ['TECH'];
const VENUE_TYPES = ['VENUE'];
const TALENT_TYPES = ['MUSICIAN', 'TECH'];

function ProtectedRoute({ children, allowedTypes }) {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return <AppShellSkeleton />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check against all user roles (supports dual-role)
  if (allowedTypes && !allowedTypes.some(t => hasRole(t))) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return <AppShellSkeleton />;
  }

  // Helper to get profile route based on user type
  const getProfileRoute = () => {
    if (!user) return '/requests';
    switch (user.profileType) {
      case 'MUSICIAN': return '/musicians/me';
      case 'TECH': return '/tech/me';
      case 'VENUE': return '/venues/me';
      default: return '/requests';
    }
  };

  // Helper to get onboarding route based on user type
  const getOnboardingRoute = () => {
    if (!user) return null;
    switch (user.profileType) {
      case 'MUSICIAN': return '/onboarding/musician';
      case 'TECH': return '/onboarding/tech';
      case 'VENUE': return '/onboarding/venue';
      default: return null;
    }
  };

  // Helper to check if user needs to complete onboarding
  const needsOnboarding = () => {
    if (!user) return false;
    // Only redirect if profileStatus is explicitly 'draft' (not null/undefined/published)
    return user.profileStatus === 'draft';
  };

  // Pre-launch coming-soon gate. When REACT_APP_COMING_SOON=true, unauthenticated
  // visitors only reach the waitlist + privacy pages; /login stays open as the bypass
  // (sign in -> session cookie -> full app). Flip the env var off to unmask at launch.
  const comingSoon = process.env.REACT_APP_COMING_SOON === 'true';
  if (comingSoon && !user) {
    return (
      <Routes>
        <Route path="/join" element={<JoinPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/join" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={user ? <Navigate to={needsOnboarding() ? getOnboardingRoute() : '/'} replace /> : <LoginPage />} />
      {/* Signup is always reachable — if a prior session exists, AuthContext.signup() clears it first. */}
      <Route path="/signup" element={<SignupPage />} />
      
      {/* Onboarding Wizards - Outside Layout */}
      <Route
        path="/onboarding/musician"
        element={
          <ProtectedRoute allowedTypes={MUSICIAN_TYPES}>
            <MusicianOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/tech"
        element={
          <ProtectedRoute allowedTypes={TECH_TYPES}>
            <TechOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/venue"
        element={
          <ProtectedRoute allowedTypes={VENUE_TYPES}>
            <VenueOnboardingPage />
          </ProtectedRoute>
        }
      />
      
      {/* How It Works - outside layout for cleaner look */}
      <Route path="/how-it-works" element={<HowItWorksPage />} />

      {/* Pricing - public, standalone (no auth) */}
      <Route path="/pricing" element={<PricingPage />} />

      {/* Join the launch community — public waitlist, standalone (no Layout) */}
      <Route path="/join" element={<JoinPage />} />

      {/* Privacy notice (waitlist) — public, standalone (no Layout) */}
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Landing Page - standalone */}
      <Route path="/landing" element={<LandingPage />} />

      {/* Home - Landing for unauthenticated users */}
      {!user && <Route path="/" element={<LandingPage />} />}
      
      {/* Redirect draft users to onboarding */}
      {user && needsOnboarding() && <Route path="/" element={<Navigate to={getOnboardingRoute()} replace />} />}

      {/* Main Layout */}
      <Route element={<Layout />}>
        {/* Home - Requests for authenticated */}
        {user && <Route path="/" element={<RequestsPage />} />}
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/requests/:id" element={<RequestsPage />} />
        <Route path="/gig/:slug" element={<GigSlugPage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />

        {/* My Interests / My Gigs - for talent */}
        <Route
          path="/my-interests"
          element={
            <ProtectedRoute allowedTypes={TALENT_TYPES}>
              <MyInterestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-gigs"
          element={
            <ProtectedRoute allowedTypes={TALENT_TYPES}>
              <MyInterestsPage />
            </ProtectedRoute>
          }
        />

        {/* Booking Confirmation - accessible for demo + authenticated users */}
        <Route
          path="/bookings/:bookingId"
          element={<BookingConfirmationPage />}
        />

        {/* Test Dashboard — non-production only */}
        {process.env.NODE_ENV !== 'production' && (
          <Route path="/test-dashboard" element={<TestDashboardPage />} />
        )}
        {/* Pending Invites */}
        <Route
          path="/invites/pending"
          element={
            <ProtectedRoute allowedTypes={TALENT_TYPES}>
              <PendingInvitesPage />
            </ProtectedRoute>
          }
        />

        {/* Saved Requests */}
        <Route
          path="/saved"
          element={
            <ProtectedRoute>
              <SavedRequestsPage />
            </ProtectedRoute>
          }
        />

        {/* Musicians */}
        <Route path="/musicians" element={<MusiciansPage />} />
        <Route path="/musicians/:id" element={<MusiciansPage />} />
        <Route
          path="/musicians/me"
          element={
            <ProtectedRoute allowedTypes={MUSICIAN_TYPES}>
              <MyMusicianProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/musicians/me/availability"
          element={
            <ProtectedRoute allowedTypes={MUSICIAN_TYPES}>
              <MusicianAvailabilityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/musicians/me/rider"
          element={
            <ProtectedRoute allowedTypes={MUSICIAN_TYPES}>
              <MyRiderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rider-check"
          element={
            <ProtectedRoute allowedTypes={MUSICIAN_TYPES}>
              <RiderCheckRunPage />
            </ProtectedRoute>
          }
        />

        {/* Tech */}
        <Route path="/tech" element={<TechPage />} />
        <Route path="/tech/:id" element={<TechPage />} />
        <Route
          path="/tech/me"
          element={
            <ProtectedRoute allowedTypes={TECH_TYPES}>
              <MyTechProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/me/availability"
          element={
            <ProtectedRoute allowedTypes={TECH_TYPES}>
              <TechAvailabilityPage />
            </ProtectedRoute>
          }
        />

        {/* Venues */}
        <Route path="/venues" element={<VenuesPage />} />
        <Route path="/venues/:id" element={<VenueDetailPage />} />
        <Route
          path="/venues/me"
          element={
            <ProtectedRoute allowedTypes={VENUE_TYPES}>
              <MyVenueProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venues/me/inventory"
          element={
            <ProtectedRoute allowedTypes={VENUE_TYPES}>
              <MyVenueBacklinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venues/requests/new"
          element={
            <ProtectedRoute allowedTypes={VENUE_TYPES}>
              <CreateRequestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venues/requests"
          element={
            <ProtectedRoute allowedTypes={VENUE_TYPES}>
              <ManageRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venues/requests/:requestId/edit"
          element={
            <ProtectedRoute allowedTypes={VENUE_TYPES}>
              <EditRequestPage />
            </ProtectedRoute>
          }
        />

        {/* Requests - Keep detail route, remove list route (moved to /) */}
      </Route>

      {/* 404 fallback — must be the last route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
